import React from "react";
import { useState, useEffect } from "react";
import api from "../Api";
import { useAuth } from "../context/authcontext";
import { useFlash } from "../context/flashcontext";

export function useTimesheetList({ projectId = null, userId = null, statusFilter = null }) {
    const { auth } = useAuth();
    const { showFlash } = useFlash();
    const [timesheets, setTimesheets] = useState([]);
    const [allTimesheets, setAllTimesheets] = useState([]); // Store the full, unfiltered timesheet list
    const [filterStatus, setFilterStatus] = useState(statusFilter !== null ? statusFilter : "");
    const [filterUser, setFilterUser] = useState("");
    const [filterProject, setFilterProject] = useState("");
    const [sortOrder, setSortOrder] = useState("asc");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [projectRole, setProjectRole] = useState(null);
    const [selectedTimesheetId, setSelectedTimesheetId] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedProjectId, setSelectedProjectId] = useState(projectId);
    const [voormanProjects, setVoormanProjects] = useState([]);

    // Fetch voorman projects and set role if found
    const fetchVoormanProjects = async () => {
        try {
            const res = await api.get(`/users/${auth.userId}/voorman-projects`);
            const projects = res.data;
            if (projects.length > 0) {
                setProjectRole("voorman");
            }
        } catch (err) {
            // ignore
        }
    };

    // Fetch role in a specific project
    const fetchRoleInProject = async (projectId) => {

        if (projectId) {
            try {
                const res = await api.get(`/projects/${projectId}/users/${auth.userId}`);
                setProjectRole(res.data.project_role);
            } catch (err) {
                setProjectRole(null);
            }
        }
    };

    // Fetch all projects where the user is assigned (zzper or voorman)
    const fetchAssignedProjects = async () => {
        try {
            const res = await api.get(`/users/${auth.userId}/projects`);
            const projects = res.data;
            // If only one project, select it
            if (projects.length === 1) {
                setSelectedProjectId(projects[0].id);
                await fetchRoleInProject(projects[0].id);
            } else if (projects.some(p => p.project_role === "zzper")) {
                // If user is zzper on any project, select that project
                const zzperProject = projects.find(p => p.project_role === "zzper");
                setSelectedProjectId(zzperProject.id);
                await fetchRoleInProject(zzperProject.id);
            } else {
                // Multiple projects, user is voorman or other role: do not auto-select
                setSelectedProjectId(null);
                setProjectRole(null);
            }
        } catch (err) {
            setProjectRole(null);
            setSelectedProjectId(null);
        }
    };

    // Fetch all projects where the user is voorman
    const fetchVoormanProjectsList = async () => {
        try {
            const res = await api.get(`/users/${auth.userId}/voorman-projects`);
            setVoormanProjects(res.data || []);
            return res.data || [];
        } catch (err) {
            setVoormanProjects([]);
            return [];
        }
    };

    // Refactored: Fetch and set projectRole/selectedProjectId first, then fetch timesheets
    const fetchRoleAndProject = async () => {
        setLoading(true);
        setError(null);
        if (projectId) {
            await fetchRoleInProject(projectId);
            setSelectedProjectId(projectId);
        } else {
            // Owner and finance users should NOT have auto-selected projects
            // They should always see ALL timesheets
            if (auth.role !== "eigenaar" && auth.role !== "financieel medewerker") {
                await fetchAssignedProjects();
                await fetchVoormanProjects();
            } else {
                // For owner/finance, explicitly set to null to ensure all timesheets are fetched
                setSelectedProjectId(null);
                setProjectRole(null);
            }
        }
    };

    // Refactored: fetchTimesheets now accepts filter values as arguments
    const fetchTimesheets = async (status = filterStatus, user = filterUser, project = filterProject, order = sortOrder) => {
        setLoading(true);
        setError(null);
        try {
            let endpoint = "";
            const effectiveProjectId = projectId || selectedProjectId;
            let fetchedTimesheets = [];

            // Owner and finance roles always see ALL timesheets
            if (!effectiveProjectId && (auth.role === "eigenaar" || auth.role === "financieel medewerker")) {
                endpoint = `/timesheets`;
                const response = await api.get(`${endpoint}?include=user,project`);
                fetchedTimesheets = response.data;
            } else if (effectiveProjectId && (auth.role === "eigenaar" || auth.role === "financieel medewerker")) {
                endpoint = `/timesheets/project/${effectiveProjectId}`;
                const response = await api.get(`${endpoint}?include=user,project`);
                fetchedTimesheets = response.data;
            } else if (projectRole === "voorman") {
                const voormanProjects = await fetchVoormanProjectsList();
                for (const project of voormanProjects) {
                    const res = await api.get(`/timesheets/project/${project.id}?include=user,project`);
                    if (Array.isArray(res.data)) {
                        fetchedTimesheets = fetchedTimesheets.concat(res.data);
                    }
                }
                const ownRes = await api.get(`/timesheets/user/${auth.userId}?include=user,project`);
                if (Array.isArray(ownRes.data)) {
                    fetchedTimesheets = fetchedTimesheets.concat(ownRes.data.filter(ts => !fetchedTimesheets.some(t => t.id === ts.id)));
                }
            } else if (effectiveProjectId && projectRole === "zzper") {
                endpoint = `/users/${auth.userId}/projects/${effectiveProjectId}/timesheets`;
                const response = await api.get(`${endpoint}?include=user,project`);
                fetchedTimesheets = response.data;
            } else {
                endpoint = `/timesheets/user/${auth.userId}`;
                const response = await api.get(`${endpoint}?include=user,project`);
                fetchedTimesheets = response.data;
            }
            setAllTimesheets(fetchedTimesheets); // Store unfiltered list for dropdowns
            // Now apply filters for display
            let data = fetchedTimesheets;
            if (status) {
                data = data.filter(ts => ts.status === status);
            }
            if (user) data = data.filter(ts => ts.user.name === user);
            if (project) data = data.filter(ts => ts.project.name === project);
            if (order === "asc") data = data.sort((a, b) => a.week_number - b.week_number);
            else data = data.sort((a, b) => b.week_number - a.week_number);
            setTimesheets(data);
            setLoading(false);
        } catch (err) {
            setError("Fout bij laden van weekstaten.");
            setLoading(false);
            console.error("[fetchTimesheets] error:", err);
        }
    };

    useEffect(() => {
        fetchRoleAndProject();
        // eslint-disable-next-line
    }, [projectId, auth.role, auth.userId]);

    // Only fetch timesheets after projectRole and selectedProjectId are resolved
    useEffect(() => {
        if (
            (auth.role === "eigenaar" || auth.role === "financieel medewerker") ||
            (projectRole && (selectedProjectId || projectId)) ||
            (projectRole === "voorman" && !selectedProjectId && !projectId)
        ) {
            fetchTimesheets(filterStatus, filterUser, filterProject, sortOrder);
        } else {
            setLoading(false);
        }
        // eslint-disable-next-line
    }, [projectRole, selectedProjectId, projectId, auth.role, auth.userId, filterStatus, filterUser, filterProject, sortOrder]);

    const handleApprove = async (id) => {
        try {
            await api.put(`/timesheets/${id}/approve`);
            fetchTimesheets();
        } catch (err) {
            setError("Fout bij goedkeuren weekstaat");
        }
    };

    const handleReject = async (id) => {
        try {
            await api.put(`/timesheets/${id}/reject`);
            fetchTimesheets();
        } catch (err) {
            setError("Fout bij afkeuren weekstaat");
        }
    };

    const confirmDelete = (id) => {
        setSelectedTimesheetId(id);
        setShowDeleteModal(true);
    };

    const handleDeleteConfirmed = async () => {
        try {
            await api.delete(`/timesheets/${selectedTimesheetId}`);
            setTimesheets(prev => prev.filter(ts => ts.id !== selectedTimesheetId));
            showFlash("Weekstaat succesvol verwijderd", "success");
        } catch (err) {
            showFlash("Fout bij verwijderen weekstaat", "error");
        } finally {
            setShowDeleteModal(false);
            setSelectedTimesheetId(null);
        }
    };

    return {
        timesheets,
        allTimesheets,
        filterStatus,
        setFilterStatus,
        filterUser,
        setFilterUser,
        filterProject,
        setFilterProject,
        sortOrder,
        setSortOrder,
        loading,
        error,
        projectRole,
        selectedTimesheetId,
        setSelectedTimesheetId,
        showDeleteModal,
        setShowDeleteModal,
        handleApprove,
        handleReject,
        confirmDelete,
        handleDeleteConfirmed,
        auth,
        projectId: selectedProjectId,
        voormanProjects
    };
}
